import { useCredentialReveal } from "../hooks/useCredentialReveal";
import { CredentialWithId, DetailsCardItem } from "../types/credentialTypes";
import { CredentialDetailsPanel } from "./CredentialDetailsPanel";

interface CredentialDetailsPanelContainerProps {
  credentialDetails: CredentialWithId | null;
  detailsCardData: DetailsCardItem[];
  selectedCredentialId: string | null;
  onEditClick?: () => void;
}

/**
 * Wraps the existing `CredentialDetailsPanel` with the password / secret-key
 * reveal state so the page only deals with selection-level concerns.
 *
 * Renders nothing until a credential is actually selected — the "no credentials"
 * and "no client selected" empty states are owned by the parent page.
 */
export const CredentialDetailsPanelContainer = ({
  credentialDetails,
  detailsCardData,
  selectedCredentialId,
  onEditClick,
}: CredentialDetailsPanelContainerProps) => {
  const {
    passwordState,
    secretKeyState,
    togglePasswordVisibility,
    toggleSecretKeyVisibility,
  } = useCredentialReveal(credentialDetails, selectedCredentialId);

  const hasSelectedCredential = !!selectedCredentialId && !!credentialDetails;

  if (!hasSelectedCredential) {
    return <div className="min-h-0 overflow-auto" />;
  }

  return (
    <div className="min-h-0 overflow-auto">
      <CredentialDetailsPanel
        detailsCardData={detailsCardData}
        passwordState={passwordState}
        secretKeyState={secretKeyState}
        hasCredentialId={!!credentialDetails?.credential_id}
        hasSecretKey={!!credentialDetails?.secret_key}
        onTogglePassword={togglePasswordVisibility}
        onToggleSecretKey={toggleSecretKeyVisibility}
        portalName={credentialDetails?.portal_name}
        username={credentialDetails?.username}
        onEditClick={onEditClick}
      />
    </div>
  );
};
